import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { SectionIntro } from '../alt/SectionIntro';
import { AgentBuilderCard, ApprovalCard, CategoryChip, ConnectionsCard, SharedAgentsCard, TemplateWall } from './agentArtefacts';
const CONNECT_IMAGE = 'https://cdn.magicpatterns.com/uploads/hJZmgqcQ1jKjsQ1YwRUstr/hf_20260906_211028_5de5584d-d238-4fb1-afb6-dba925712826.png';
const EASE = [0.16, 1, 0.3, 1] as const;
type PanelProps = {
  children: React.ReactNode;
  className?: string;
  tint?: 'white' | 'peach' | 'sand';
  delay?: number;
};
function Panel({
  children,
  className = '',
  tint = 'white',
  delay = 0
}: PanelProps) {
  const reduce = useReducedMotion();
  const surface = tint === 'peach' ? 'bg-[#FBF7F1]' : tint === 'sand' ? 'bg-[#F7F3ED]' : 'bg-white';
  return <motion.article initial={reduce ? false : {
    opacity: 0,
    y: 18
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-90px'
  }} transition={{
    duration: 0.3,
    delay,
    ease: EASE
  }} className={`relative flex min-w-0 flex-col overflow-hidden rounded-[22px] border border-[#E7E0D4] ${surface} shadow-[0_1px_2px_rgba(43,33,26,0.04),0_24px_60px_-40px_rgba(43,33,26,0.35)] ${className}`} data-id="element-278" data-mp="1pqmdghtjgm6o">
      {children}
    </motion.article>;
}
type PanelCopyProps = {
  title: string;
  copy: string;
  className?: string;
  children?: React.ReactNode;
};
function PanelCopy({
  title,
  copy,
  className = '',
  children
}: PanelCopyProps) {
  return <div className={className} data-id="element-279" data-mp="yhqundpcjgb0">
      <h3 className="font-title text-[22px] font-bold leading-[1.14] tracking-[-0.025em] text-[#2B211A] md:text-[24px]" data-id="element-280" data-mp="yj1y1cevqm1l">
        {title}.
      </h3>
      <p className="mt-2.5 max-w-[42ch] text-[14px] leading-[1.65] text-[#6B5D52] md:text-[14.5px]" data-id="element-281" data-mp="orfp5mq0kz20">
        {copy}
      </p>
      {children}
    </div>;
}
function ApprovalVisual() {
  return <div className="relative flex h-[250px] min-h-0 items-center justify-center overflow-hidden rounded-[18px] bg-[#F3EEE7] px-5 md:min-h-[290px] md:px-7 xl:h-full xl:rounded-l-none xl:rounded-r-[21px]" data-id="element-282" data-mp="et9pzcvle70u">
      <div aria-hidden="true" className="dot-matrix pointer-events-none absolute inset-0 opacity-[0.12]" data-id="element-283" data-mp="2coyeblop5e6a" />
      <div className="relative w-full max-w-[300px]" data-id="element-284" data-mp="27plrv21qm948">
        <ApprovalCard data-id="element-285" data-mp="27oaqt9zoffgt" />
      </div>
    </div>;
}
function ConnectionsVisual() {
  return <div className="relative h-[250px] overflow-hidden rounded-[18px] bg-[#EAE4DC] md:min-h-[300px] lg:h-full lg:rounded-l-none lg:rounded-r-[21px]" data-id="element-286" data-mp="22p7i9fkhmr43">
      <img src={CONNECT_IMAGE} alt="" aria-hidden="true" loading="lazy" className="absolute inset-0 size-full object-cover object-[18%_70%] opacity-40 saturate-[0.55]" data-id="element-287" data-mp="2vfog172j6uxk" />
      <div aria-hidden="true" className="absolute inset-0 bg-[#F7F3ED]/35" data-id="element-288" data-mp="2mehx6rcet815" />
      <div className="absolute bottom-5 left-5 right-5 sm:right-auto sm:w-[230px] md:bottom-6 md:left-6" data-id="element-289" data-mp="1f27tr0maz7ij">
        <ConnectionsCard data-id="element-290" data-mp="1azuvsydssdwb" />
      </div>
    </div>;
}
export function AgentFeatureMosaic() {
  return <div data-id="element-291" data-mp="3mohm5cduobea">
      <SectionIntro align="left" className="max-w-[720px]" data-id="element-292" data-mp="3morbcxr2meix">
        <h2 className="font-title text-[34px] font-bold leading-[1.08] tracking-[-0.03em] text-[#2B211A] md:text-[52px]" data-id="element-293" data-mp="3qos0bcbsq848">
          Built for the work that repeats.
        </h2>
        <p className="mt-5 max-w-[54ch] text-[16.5px] leading-relaxed text-[#6B5D52] md:text-lg" data-id="element-294" data-mp="3vmbdsa0o6gv4">
          Move past one-off prompts. Hand a recurring responsibility to an agent that knows your
          company, reads the systems you already run on, and reports back where your team already
          works.
        </p>
      </SectionIntro>

      <div className="mt-12 grid grid-cols-1 gap-4 md:mt-16 lg:grid-cols-12 lg:gap-5" data-id="element-295" data-mp="i8zw4qykbugm">
        <Panel tint="peach" className="lg:col-span-7 lg:min-h-[340px]" data-id="element-296" data-mp="n8unevm92m27">
          <div className="grid h-full min-w-0 grid-cols-1 xl:grid-cols-[minmax(0,0.9fr)_minmax(240px,1.1fr)]" data-id="element-297" data-mp="2k0miugbf937a">
            <PanelCopy title="Create an agent in minutes" copy="Describe the role, the priorities and the outcomes it should care about — no code, no flowchart. Then choose where it runs, what it is allowed to reach, and which model sits behind it." className="p-6 sm:p-7 md:pr-5" data-id="element-298" data-mp="2ozfvy7msqs6x">
              <div className="mt-6 flex flex-wrap gap-2" data-id="element-299" data-mp="23me2qa6b55p0">
                <CategoryChip category="marketing" data-id="element-300" data-mp="1yovr2c0wocdb" />
                <CategoryChip category="sales" data-id="element-301" data-mp="1yp5k68h8yzn8" />
                <CategoryChip category="people" data-id="element-302" data-mp="1ayrvmrxvfixe" />
              </div>
            </PanelCopy>
            <div className="flex min-w-0 items-center justify-center px-5 pb-6 sm:px-6 xl:pb-0" data-id="element-303" data-mp="1606wywy9p7b2">
              <div className="w-full max-w-[275px]" data-id="element-304" data-mp="1kokxh187ojog">
                <AgentBuilderCard data-id="element-305" data-mp="1kqe44gitr3wg" />
              </div>
            </div>
          </div>
        </Panel>

        <Panel className="lg:col-span-5 lg:min-h-[340px]" delay={0.07} data-id="element-306" data-mp="1fwjmrjqoz10j">
          <PanelCopy title="Start fast with pre-built templates" copy="Kick off with agents designed for real jobs across sales, marketing, support, finance and HR. Then change anything about them — the brief, the schedule, the tools — to match the way your team actually works." className="p-6 pb-0 sm:p-7 sm:pb-0" data-id="element-307" data-mp="s7icdqyatfsm" />
          {/* One horizontal shelf of four templates. The internal canvas is
           deliberately wide before being reduced, so moving from 2×2 to four
           columns does NOT make each card smaller. The shelf sits higher in
           its viewport and gets enough height for the card shadows and pointer
           to finish rather than being clipped at the panel's bottom edge. */}
          <div className="relative mx-auto mt-auto h-[132px] w-full overflow-hidden" data-id="element-308" data-mp="3mk0s6m3bsfjj">
            <div className="absolute -top-3 left-1/2 w-[900px] max-w-none origin-top -translate-x-1/2 scale-[0.46]" data-id="element-309" data-mp="38ooohp76zeqh">
              <TemplateWall data-id="element-310" data-mp="1x7gl978a84nw" />
            </div>
          </div>
        </Panel>

        <Panel tint="sand" className="lg:col-span-12 lg:min-h-[300px]" data-id="element-311" data-mp="1x9jmknevxk7x">
          <div className="grid h-full min-w-0 grid-cols-1 lg:grid-cols-[minmax(320px,0.92fr)_minmax(0,1.08fr)]" data-id="element-312" data-mp="219tsy16lacnb">
            <PanelCopy title="Securely connect your work tools" copy="Generic AI doesn't know your business; a Giga agent does. Connections are made once, centrally, and every agent reaches your systems through them — never through somebody's personal login." className="p-6 sm:p-7 md:self-center md:p-9 lg:p-11" data-id="element-313" data-mp="7pjnb0rb8qba" />
            <ConnectionsVisual data-id="element-314" data-mp="3l696xqy65jwp" />
          </div>
        </Panel>

        <Panel className="lg:col-span-7 lg:min-h-[330px]" data-id="element-315" data-mp="3g64tk9sk55ap">
          <div className="grid h-full min-w-0 grid-cols-1 xl:grid-cols-[minmax(250px,0.9fr)_minmax(0,1.1fr)]" data-id="element-316" data-mp="3g4tmo13tfhfd">
            <PanelCopy title="Stay in control while agents work" copy="Before an agent sends a message, files a ticket or updates a record, it stops and shows you exactly what is about to happen. Approve it once, allow it always, or decline — every time, on every action." className="p-6 sm:p-7 md:self-center md:pr-5" data-id="element-317" data-mp="3b94jzowi1keu" />
            <ApprovalVisual data-id="element-318" data-mp="1cwt7ji2tmzk6" />
          </div>
        </Panel>

        <Panel tint="peach" className="lg:col-span-5 lg:min-h-[330px]" delay={0.07} data-id="element-319" data-mp="1gyoaer0ehl02">
          <PanelCopy title="Share an agent with the whole team" copy="An agent built once belongs to the group, not to the person who made it. Access follows the group it sits in, so the right people get it on day one and nobody quietly rebuilds the same thing." className="p-6 pb-0 sm:p-7 sm:pb-0" data-id="element-320" data-mp="1lpwnpy4upqii" />
          {/* The directory previously filled the card from edge to edge and
           competed with the claim. At half scale it reads as a product
           vignette, with enough quiet ground around it to see the stack. */}
          <div className="relative mx-auto mt-auto h-[155px] w-full overflow-hidden" data-id="element-321" data-mp="17wezw154trmo">
            <div className="absolute left-1/2 top-2 w-[360px] max-w-none origin-top -translate-x-1/2 scale-[0.46]" data-id="element-322" data-mp="2eebxa557rgmg">
              <SharedAgentsCard data-id="element-323" data-mp="2jfrfzqjx5b1o" />
            </div>
          </div>
        </Panel>
      </div>
    </div>;
}